import { notifier } from "../../core/notifier/notifier";
import { spinner } from "../../util/spinner/spinner";
import {
    JOB_SCHEDULE_TYPE_VALUES,
    JobsDashboardScheduleType,
    JobsDashboardStatus,
    JobsDashboardUtils,
    type JobsDashboardRecord,
    type JobsDashboardValue,
} from "./jobs-dashboard-utils";
import type { JobsDashboardForm } from "./jobs-dashboard-form";
import type { JobsDashboardApi } from "./jobs-dashboard-api";
import type { JobsDashboardRender } from "./jobs-dashboard-render";

type JobsDashboardHandlerValue = JobsDashboardValue;

type JobRecord = JobsDashboardRecord;
type JobPatch = JobsDashboardRecord;
type JobHistoryEntry = JobsDashboardRecord;

interface DashboardResponseBody {
    success?: number;
    message?: JobsDashboardHandlerValue;
    data?: {
        jobs?: JobRecord[];
        error_code?: string;
    } & JobsDashboardRecord;
}

interface JobExecutionResult {
    ok: boolean;
    summary: string;
    detail: JobsDashboardRecord;
}

interface JobCompletionState {
    done: boolean;
    status: string;
}

export class JobsDashboardHandler {
    private readonly form: JobsDashboardForm;

    private readonly api: JobsDashboardApi;

    private readonly render: JobsDashboardRender;

    private jobs: JobRecord[] = [];

    private readonly historyByJob = new Map<string, JobHistoryEntry[]>();

    private readonly editingJobKeys = new Set<string>();

    private readonly jobPollingTimers = new Map<string, ReturnType<typeof setInterval>>();

    public constructor(form: JobsDashboardForm, api: JobsDashboardApi, render: JobsDashboardRender) {
        this.form = form;
        this.api = api;
        this.render = render;
    }

    private setJobs(jobs: JobRecord[]): void {
        this.jobs = JobsDashboardUtils.sortJobsBySchedule(jobs);
    }

    private notifySuccess(message: string, translateKey?: string): void {
        notifier.success(message, translateKey ? { translateKey } : undefined);
    }

    private notifyWarn(message: string, translateKey?: string): void {
        notifier.warn(message, translateKey ? { translateKey } : undefined);
    }

    private notifyError(message: string, translateKey?: string): void {
        notifier.error(message, translateKey ? { translateKey } : undefined);
    }

    private isAbortConflict(responseStatus: number, errorCode?: string): boolean {
        return responseStatus === 409 || errorCode === "job_not_running" || errorCode === "running_process_not_tracked";
    }

    private getAbortConflictTranslateKey(errorCode?: string): string {
        if (errorCode === "job_not_running") {
            return "jobs_dashboard_job_not_running";
        }

        if (errorCode === "running_process_not_tracked") {
            return "jobs_dashboard_running_process_not_tracked";
        }

        return "jobs_dashboard_abort_conflict";
    }

    public async bootstrap(): Promise<void> {
        await this.handleRefresh();
    }

    public async handleRefresh(): Promise<void> {
        this.render.setStatus("Loading jobs...");

        try {
            const response = await this.api.fetchJobs();
            if (!response.ok) {
                throw new Error(`Failed to load jobs (${response.status})`);
            }

            const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;
            if (!body || body.success !== 1) {
                throw new Error(JobsDashboardUtils.toText(body?.message || "Failed to load jobs"));
            }

            this.setJobs(Array.isArray(body?.data?.jobs) ? body.data.jobs : []);
            this.reconcileEditingState();
            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
            this.render.setStatus(`Loaded ${this.jobs.length} job(s)`);
        } catch (error) {
            this.jobs = [];
            this.editingJobKeys.clear();
            this.render.renderList([], this.historyByJob, this.editingJobKeys);
            this.render.setStatus("Failed to load jobs");
            const message = JobsDashboardUtils.toText(error instanceof Error ? error.message : "Failed to load jobs");
            this.render.setLatestResult(`Load error: ${message}`);
            this.notifyError(message, "jobs_dashboard_load_failed");
        }
    }

    public async executeJob(jobKey: string, dryRun: boolean): Promise<JobExecutionResult> {
        const startedAt = new Date();

        try {
            const response = await this.api.runJob(jobKey, dryRun);
            const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;
            const endedAt = new Date();
            const elapsedMs = Math.max(0, endedAt.getTime() - startedAt.getTime());

            if (!response.ok || !body || body.success !== 1) {
                const message = JobsDashboardUtils.toText(body?.message || `Run failed (${response.status})`);
                return {
                    ok: false,
                    summary: `Error: ${message}`,
                    detail: {
                        ok: false,
                        job_key: jobKey,
                        dry_run: dryRun,
                        http_status: response.status,
                        started_at: startedAt.toISOString(),
                        ended_at: endedAt.toISOString(),
                        elapsed_ms: elapsedMs,
                        message,
                        data: body?.data ?? null,
                        payload: JSON.stringify(body ?? null),
                    },
                };
            }

            return {
                ok: true,
                summary: JobsDashboardUtils.summarizeResult(body),
                detail: {
                    ok: true,
                    job_key: jobKey,
                    dry_run: dryRun,
                    http_status: response.status,
                    started_at: startedAt.toISOString(),
                    ended_at: endedAt.toISOString(),
                    elapsed_ms: elapsedMs,
                    message: JobsDashboardUtils.toText(body?.message || ""),
                    data: body?.data ?? null,
                    payload: JSON.stringify(body ?? null),
                },
            };
        } catch (error) {
            const endedAt = new Date();
            const elapsedMs = Math.max(0, endedAt.getTime() - startedAt.getTime());
            const message = JobsDashboardUtils.toText(error instanceof Error ? error.message : "Run failed");

            return {
                ok: false,
                summary: `Error: ${message}`,
                detail: {
                    ok: false,
                    job_key: jobKey,
                    dry_run: dryRun,
                    http_status: null,
                    started_at: startedAt.toISOString(),
                    ended_at: endedAt.toISOString(),
                    elapsed_ms: elapsedMs,
                    message,
                    data: null,
                    payload: null,
                },
            };
        }
    }

    public async handleRunAllClick(): Promise<void> {
        const dryRun = false;
        const runAllButton = this.form.getRunAllButton();
        if (runAllButton instanceof HTMLButtonElement) {
            spinner.showButtonLoader(runAllButton, { translateKey: "spinner_proccesing" });
        }

        const executableJobs = this.jobs.filter((job) => {
            const jobKey = JobsDashboardUtils.normalizeJobKey(job.job_key);
            return jobKey !== "";
        });
        const activeJobs = executableJobs.filter((job) => JobsDashboardUtils.toBool(job.is_active));
        const targetJobs = JobsDashboardUtils.sortJobsBySchedule(activeJobs.length > 0 ? activeJobs : executableJobs);

        if (targetJobs.length === 0) {
            this.render.setLatestResult("No active jobs to run.");
            if (runAllButton instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(runAllButton);
            }
            return;
        }

        this.render.setLatestResult(`Running ${targetJobs.length} job(s)...`);

        let successCount = 0;
        let failedCount = 0;

        try {
            for (let index = 0; index < targetJobs.length; index += 1) {
                const job = targetJobs[index];
                const jobKey = JobsDashboardUtils.normalizeJobKey(job.job_key);
                if (!jobKey) {
                    continue;
                }

                this.render.setLatestResult(`(${index + 1}/${targetJobs.length}) Running ${jobKey}...`);

                // Local-only optimistic update for immediate UI feedback (no PUT):
                // the server itself flips job_status to "running" once .../run
                // actually launches the job, and the guard added there for
                // ISS-04-096's double-run prevention treats job_status="running"
                // as authoritative. A PUT here would race that guard and make
                // every run request see itself as already running.
                const nowIso = JobsDashboardUtils.nowIso();
                this.updateJobState(jobKey, {
                    job_status: JobsDashboardStatus.Running,
                    started_at: nowIso,
                    ended_at: null,
                });

                const result = await this.executeJob(jobKey, dryRun);
                const completion = await this.waitForJobCompletion(jobKey);
                const completionSummary = completion.done
                    ? `status=${completion.status}`
                    : `wait_timeout(last_status=${completion.status})`;

                this.pushHistory(jobKey, {
                    at: JobsDashboardUtils.nowIso(),
                    summary: `${result.summary} (${completionSummary})`,
                    detail: {
                        ...result.detail,
                        completion: {
                            done: completion.done,
                            status: completion.status,
                        },
                    },
                });

                if (result.ok && completion.done && completion.status === JobsDashboardStatus.Finished) {
                    successCount += 1;
                } else {
                    failedCount += 1;
                }
            }

            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);

            const summary = `Run all finished: success=${successCount}, failed=${failedCount}, dry_run=${String(dryRun)}`;
            this.render.setLatestResult(summary);

            if (failedCount > 0) {
                this.notifyWarn(summary, "jobs_dashboard_run_all_finished_with_failures");
            } else {
                this.notifySuccess(summary, "jobs_dashboard_run_all_finished");
            }
        } finally {
            if (runAllButton instanceof HTMLButtonElement) {
                spinner.hideButtonLoader(runAllButton);
            }
        }
    }

    public async waitForJobCompletion(jobKey: string): Promise<JobCompletionState> {
        const intervalMs = 3000;
        const timeoutMs = 15 * 60 * 1000;
        const startedAt = Date.now();
        let lastStatus = JobsDashboardStatus.Running;

        while (Date.now() - startedAt < timeoutMs) {
            try {
                const response = await this.api.fetchJobs();
                if (response.ok) {
                    const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;
                    if (body && body.success === 1 && Array.isArray(body?.data?.jobs)) {
                        this.setJobs(body.data.jobs);
                        this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);

                        const current = this.jobs.find(
                            (job) => JobsDashboardUtils.normalizeJobKey(job.job_key) === jobKey,
                        );
                        const jobStatus = JobsDashboardUtils.normalizeStatus(current?.job_status);
                        if (jobStatus) {
                            lastStatus = jobStatus;
                        }

                        if (jobStatus === JobsDashboardStatus.Finished || jobStatus === JobsDashboardStatus.Failed) {
                            return {
                                done: true,
                                status: jobStatus,
                            };
                        }
                    }
                }
            } catch {
                // Keep polling until timeout.
            }

            await this.delay(intervalMs);
        }

        return {
            done: false,
            status: lastStatus,
        };
    }

    public async delay(ms: number): Promise<void> {
        await new Promise<void>((resolve) => {
            setTimeout(resolve, ms);
        });
    }

    public async handleListClick(event: MouseEvent): Promise<void> {
        if (this.handleEditToggleClick(event)) {
            return;
        }

        if (await this.handleAbortClick(event)) {
            return;
        }

        if (await this.handleSaveClick(event)) {
            return;
        }

        await this.handleRunClick(event);
    }

    public handleEditToggleClick(event: MouseEvent): boolean {
        const button = this.form.getEditButton(event.target);
        if (!(button instanceof HTMLButtonElement)) {
            return false;
        }

        const jobKey = JobsDashboardUtils.normalizeJobKey(button.dataset.jobKey);
        if (!jobKey) {
            return true;
        }

        if (this.editingJobKeys.has(jobKey)) {
            this.editingJobKeys.delete(jobKey);
            this.render.setLatestResult(`${jobKey}: edit cancelled`);
        } else {
            this.editingJobKeys.add(jobKey);
            this.render.setLatestResult(`${jobKey}: edit mode`);
        }

        this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
        return true;
    }

    public async handleSaveClick(event: MouseEvent): Promise<boolean> {
        const button = this.form.getSaveButton(event.target);
        if (!(button instanceof HTMLButtonElement)) {
            return false;
        }

        const row = this.form.getRow(button);
        const jobKey = JobsDashboardUtils.normalizeJobKey(button.dataset.jobKey);
        if (!row || !jobKey) {
            return true;
        }

        const currentJob = this.jobs.find((job) => JobsDashboardUtils.normalizeJobKey(job.job_key) === jobKey);

        const scheduleType = JobsDashboardUtils.toText(this.form.getScheduleTypeSelect(row)?.value || "")
            .trim()
            .toLowerCase();
        const scheduleTimeRaw = JobsDashboardUtils.toText(this.form.getScheduleTimeInput(row)?.value || "").trim();
        const scheduleDayInput = this.form.getScheduleDayInput(row);
        const scheduleDayRaw = JobsDashboardUtils.toText(scheduleDayInput?.value || "").trim();
        const currentScheduleDayRaw = JobsDashboardUtils.toText(currentJob?.schedule_day ?? "").trim();
        const effectiveScheduleDayRaw = scheduleDayRaw !== "" ? scheduleDayRaw : currentScheduleDayRaw;
        const isActiveRaw = JobsDashboardUtils.toText(this.form.getIsActiveSelect(row)?.value || "1").trim();

        if (!JOB_SCHEDULE_TYPE_VALUES.includes(scheduleType as JobsDashboardScheduleType)) {
            this.notifyError("Invalid schedule type", "jobs_dashboard_invalid_schedule_type");
            return true;
        }

        if (
            (scheduleType === JobsDashboardScheduleType.Daily || scheduleType === JobsDashboardScheduleType.Monthly) &&
            !/^\d{2}:\d{2}$/.test(scheduleTimeRaw)
        ) {
            this.notifyError("Schedule time is required in HH:MM format", "jobs_dashboard_invalid_schedule_time");
            return true;
        }

        if (scheduleType === JobsDashboardScheduleType.Monthly) {
            const day = Number.parseInt(effectiveScheduleDayRaw, 10);
            if (!Number.isInteger(day) || day < 1 || day > 31) {
                this.notifyError("Monthly jobs require a day between 1 and 31", "jobs_dashboard_invalid_schedule_day");
                return true;
            }
        }

        spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });

        const payload: JobPatch = {
            schedule_type: scheduleType,
            schedule_time: scheduleType === JobsDashboardScheduleType.Hourly ? null : scheduleTimeRaw,
            schedule_day:
                scheduleType === JobsDashboardScheduleType.Monthly
                    ? Number.parseInt(effectiveScheduleDayRaw, 10)
                    : null,
            is_active: isActiveRaw === "1" ? 1 : 0,
        };

        try {
            const response = await this.api.updateJob(jobKey, payload);
            const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;

            if (!response.ok || !body || body.success !== 1) {
                const message = JobsDashboardUtils.toText(body?.message || `Save failed (${response.status})`);
                this.render.setLatestResult(`${jobKey}: ${message}`);
                this.notifyError(message, "jobs_dashboard_save_failed");
                return true;
            }

            const updatedJob = body?.data?.job;
            if (updatedJob && typeof updatedJob === "object" && !Array.isArray(updatedJob)) {
                this.updateJobState(jobKey, updatedJob as JobsDashboardRecord);
            } else {
                this.updateJobState(jobKey, payload);
            }

            this.editingJobKeys.delete(jobKey);

            this.render.setLatestResult(`${jobKey}: saved`);
            this.notifySuccess(`${jobKey}: saved`, "jobs_dashboard_saved");
            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
            return true;
        } catch (error) {
            const message = JobsDashboardUtils.toText(error instanceof Error ? error.message : "Save failed");
            this.render.setLatestResult(`${jobKey}: ${message}`);
            this.notifyError(message, "jobs_dashboard_save_failed");
            return true;
        } finally {
            spinner.hideButtonLoader(button);
        }
    }

    public async handleRunClick(event: MouseEvent): Promise<boolean> {
        const button = this.form.getRunButton(event.target);
        if (!(button instanceof HTMLButtonElement)) {
            return false;
        }

        const jobKey = JobsDashboardUtils.normalizeJobKey(button.dataset.jobKey);
        if (!jobKey) {
            return true;
        }

        const dryRun = false;
        spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });
        this.render.setLatestResult(`Running ${jobKey}...`);

        // Local-only optimistic update for immediate UI feedback (no PUT):
        // the server itself flips job_status to "running" once .../run
        // actually launches the job, and the guard added there for
        // ISS-04-096's double-run prevention treats job_status="running" as
        // authoritative. A PUT here would race that guard and make every run
        // request see itself as already running.
        const nowIso = JobsDashboardUtils.nowIso();
        this.updateJobState(jobKey, {
            job_status: JobsDashboardStatus.Running,
            started_at: nowIso,
            ended_at: null,
        });
        this.startJobPolling(jobKey);

        try {
            const result = await this.executeJob(jobKey, dryRun);
            this.pushHistory(jobKey, {
                at: JobsDashboardUtils.nowIso(),
                summary: result.summary,
                detail: result.detail,
            });

            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
            this.render.setLatestResult(`${jobKey}: ${result.summary}`);

            if (result.ok) {
                this.notifySuccess(`${jobKey}: ${result.summary}`, "jobs_dashboard_job_execution_queued");
            } else {
                this.notifyError(result.summary, "jobs_dashboard_run_failed");
            }
        } catch (error) {
            const message = JobsDashboardUtils.toText(error instanceof Error ? error.message : "Run failed");
            this.render.setLatestResult(`${jobKey}: Error: ${message}`);
            this.notifyError(message, "jobs_dashboard_run_failed");
        } finally {
            spinner.hideButtonLoader(button);
        }

        return true;
    }

    public async handleAbortClick(event: MouseEvent): Promise<boolean> {
        const button = this.form.getAbortButton(event.target);
        if (!(button instanceof HTMLButtonElement)) {
            return false;
        }

        const jobKey = JobsDashboardUtils.normalizeJobKey(button.dataset.jobKey);
        if (!jobKey) {
            return true;
        }

        const confirmed = globalThis.confirm(`Abort running job "${jobKey}"?`);
        if (!confirmed) {
            return true;
        }

        spinner.showButtonLoader(button, { translateKey: "spinner_proccesing" });
        this.render.setLatestResult(`Aborting ${jobKey}...`);

        try {
            const response = await this.api.abortJob(jobKey);
            const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;
            const errorCode = JobsDashboardUtils.toText(body?.data?.error_code || "")
                .trim()
                .toLowerCase();

            if (!response.ok || !body || body.success !== 1) {
                const message = JobsDashboardUtils.toText(body?.message || `Abort failed (${response.status})`);
                this.render.setLatestResult(`${jobKey}: ${message}`);
                if (this.isAbortConflict(response.status, errorCode)) {
                    this.notifyWarn(message, this.getAbortConflictTranslateKey(errorCode));
                    await this.handleRefresh();
                } else {
                    this.notifyError(message, "jobs_dashboard_failed_to_abort");
                }
                return true;
            }

            const updatedJob = body?.data?.job;
            if (updatedJob && typeof updatedJob === "object" && !Array.isArray(updatedJob)) {
                this.updateJobState(jobKey, updatedJob as JobsDashboardRecord);
            } else {
                this.updateJobState(jobKey, {
                    job_status: JobsDashboardStatus.Failed,
                    ended_at: JobsDashboardUtils.nowIso(),
                });
            }

            this.stopJobPolling(jobKey);
            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
            this.render.setLatestResult(`${jobKey}: aborted`);
            this.notifySuccess(`${jobKey}: aborted`, "jobs_dashboard_aborted");
            return true;
        } catch (error) {
            const message = JobsDashboardUtils.toText(error instanceof Error ? error.message : "Abort failed");
            this.render.setLatestResult(`${jobKey}: ${message}`);
            this.notifyError(message, "jobs_dashboard_failed_to_abort");
            return true;
        } finally {
            spinner.hideButtonLoader(button);
        }
    }

    public startJobPolling(jobKey: string): void {
        if (this.jobPollingTimers.has(jobKey)) {
            return;
        }

        const intervalMs = 10000;
        const timer = setInterval(async () => {
            try {
                const response = await this.api.fetchJobs();
                if (!response.ok) {
                    return;
                }

                const body = (await response.json().catch(() => null)) as DashboardResponseBody | null;
                if (!body || body.success !== 1 || !Array.isArray(body?.data?.jobs)) {
                    return;
                }

                this.setJobs(body.data.jobs);
                this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);

                const current = this.jobs.find((job) => JobsDashboardUtils.normalizeJobKey(job.job_key) === jobKey);
                const jobStatus = JobsDashboardUtils.normalizeStatus(current?.job_status);
                if (jobStatus === JobsDashboardStatus.Finished || jobStatus === JobsDashboardStatus.Failed) {
                    this.stopJobPolling(jobKey);
                    this.render.setLatestResult(`${jobKey}: ${jobStatus}`);
                    if (jobStatus === JobsDashboardStatus.Finished) {
                        this.notifySuccess(`${jobKey}: ${JobsDashboardStatus.Finished}`, "jobs_dashboard_finished");
                    } else {
                        this.notifyError(`${jobKey}: ${JobsDashboardStatus.Failed}`, "jobs_dashboard_failed");
                    }
                }
            } catch {
                // Keep polling.
            }
        }, intervalMs);

        this.jobPollingTimers.set(jobKey, timer);
    }

    public stopJobPolling(jobKey: string): void {
        const timer = this.jobPollingTimers.get(jobKey);
        if (timer) {
            clearInterval(timer);
            this.jobPollingTimers.delete(jobKey);
        }
    }

    public updateJobState(jobKey: string, patch: JobPatch): void {
        let changed = false;
        this.setJobs(
            this.jobs.map((job) => {
                if (JobsDashboardUtils.normalizeJobKey(job.job_key) !== jobKey) {
                    return job;
                }

                changed = true;
                return {
                    ...job,
                    ...patch,
                };
            }),
        );

        if (changed) {
            this.render.renderList(this.jobs, this.historyByJob, this.editingJobKeys);
        }
    }

    public reconcileEditingState(): void {
        const currentJobKeys = new Set(this.jobs.map((job) => JobsDashboardUtils.normalizeJobKey(job.job_key)));
        for (const jobKey of Array.from(this.editingJobKeys)) {
            if (!currentJobKeys.has(jobKey)) {
                this.editingJobKeys.delete(jobKey);
            }
        }
    }

    public pushHistory(jobKey: string, entry: JobHistoryEntry): void {
        const current = this.historyByJob.get(jobKey) ?? [];
        current.unshift(entry);
        this.historyByJob.set(jobKey, current.slice(0, 20));
    }
}
